import { findRegimen, type RegimenLookup } from "@/data/meal-plan";
import { DAYS_OF_WEEK, type DayOfWeek, type WeekPlan } from "@/types/meal-plan-state";
import { getMealChoices } from "./random-plan";

export interface AggregatedItem {
  name: string;
  totalGrams: number;
  totalUnits: number;
  unit: string;
  category: string;
}

/** Count words a portion may use, by spelling → the unit Compras shows. */
const PORTION_UNITS: Record<string, string> = {
  und: "und",
  cdta: "cdta",
  cdtas: "cdta",
  cda: "cda",
  cdas: "cda",
  tz: "tz",
  paquete: "paquete",
  paquetes: "paquete",
  botella: "botella",
  botellas: "botella",
  lata: "lata",
  latas: "lata",
  opcion: "opción",
  "opción": "opción",
};

/** Weights and volumes: not counts. An option measured this way needs `grams`. */
const MEASURES = new Set(["g", "gr", "kg", "ml", "l"]);

const FRACTION_GLYPHS: Record<string, number> = { "½": 0.5, "¼": 0.25, "¾": 0.75 };

/** "2", "1.5", "½", "1½", "1/2". */
function parseCount(token: string): number {
  const slash = token.match(/^(\d+)\/(\d+)$/);
  if (slash) return Number(slash[1]) / Number(slash[2]);
  const glyph = token.match(/^(\d*)([½¼¾])$/);
  if (glyph) return Number(glyph[1] || 0) + FRACTION_GLYPHS[glyph[2]!]!;
  return Number(token);
}

/**
 * "2 cdtas" → 2 cdta, "1½ und" → 1.5 und, "1 1/2 tz" → 1.5 tz,
 * "1 botella (330 ml)" → 1 botella, "2 scoops" → 2 und. Null when the portion doesn't start with a
 * count (or is a weight like "100 g").
 */
function parsePortionUnit(portion: string): { count: number; unit: string } | null {
  const match = portion.match(
    /^((?:\d+(?:\.\d+)?|\d*[½¼¾]|\d+\/\d+)(?:\s+(?:\d+\/\d+|[½¼¾]))?)\s+([a-záéíóúñ]+)/i,
  );
  if (!match) return null;
  const word = match[2]!.toLowerCase();
  if (MEASURES.has(word)) return null;
  const count = match[1]!.split(/\s+/).reduce((sum, token) => sum + parseCount(token), 0);
  return { count, unit: PORTION_UNITS[word] ?? "und" };
}

/** Total amount of each ingredient picked across a week's days. */
export function aggregateWeek(
  weekPlan: WeekPlan,
  lookup: RegimenLookup = findRegimen,
): AggregatedItem[] {
  const map = new Map<
    string,
    { totalGrams: number; totalUnits: number; unit: string; category: string }
  >();

  for (const { id: day } of DAYS_OF_WEEK) {
    const dayPlan = weekPlan[day as DayOfWeek];
    if (!dayPlan.regimenId || !dayPlan.meals) continue;

    const regimen = lookup(dayPlan.regimenId);
    if (!regimen) continue;

    for (let mi = 0; mi < regimen.meals.length; mi++) {
      const meal = regimen.meals[mi]!;
      const mealSels = dayPlan.meals[mi];
      if (!mealSels) continue;
      const choices = getMealChoices(meal, dayPlan.mealMenus?.[mi]);

      for (let ci = 0; ci < choices.length; ci++) {
        const group = choices[ci]!;
        const cgSels = mealSels[ci];
        if (!cgSels || cgSels.skipped) continue;

        for (const sel of cgSels.selections) {
          const opt = group.options.find((o) => o.id === sel.optionId);
          if (!opt) continue;

          const key = opt.name;
          const existing = map.get(key) ?? {
            totalGrams: 0,
            totalUnits: 0,
            unit: "",
            category: group.category,
          };

          if (opt.grams) {
            existing.totalGrams += opt.grams * sel.count;
          } else {
            const parsed = parsePortionUnit(opt.portion);
            if (parsed) {
              existing.totalUnits += parsed.count * sel.count;
              existing.unit = parsed.unit;
            } else {
              existing.totalUnits += sel.count;
              existing.unit = "und";
            }
          }

          map.set(key, existing);
        }
      }
    }
  }

  const items: AggregatedItem[] = [];
  for (const [name, data] of map) {
    items.push({ name, ...data });
  }

  return items.sort(byCategoryThenName);
}

/**
 * Compras with friends: sum several people's `aggregateWeek` lists by name.
 * The inputs are left untouched.
 */
export function mergeAggregatedItems(...itemArrays: AggregatedItem[][]): AggregatedItem[] {
  const map = new Map<string, AggregatedItem>();

  for (const items of itemArrays) {
    for (const item of items) {
      const existing = map.get(item.name);
      if (existing) {
        existing.totalGrams += item.totalGrams;
        existing.totalUnits += item.totalUnits;
        if (!existing.unit && item.unit) existing.unit = item.unit;
      } else {
        map.set(item.name, { ...item });
      }
    }
  }

  return Array.from(map.values()).sort(byCategoryThenName);
}

const CATEGORY_ORDER: Record<string, number> = {
  protein: 0,
  carb: 1,
  fruit: 2,
  vegetable: 3,
  fat: 4,
};

function byCategoryThenName(a: AggregatedItem, b: AggregatedItem): number {
  const catDiff = (CATEGORY_ORDER[a.category] ?? 99) - (CATEGORY_ORDER[b.category] ?? 99);
  if (catDiff !== 0) return catDiff;
  return a.name.localeCompare(b.name);
}
